import { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Portal,
  type FormLabelProps,
} from '@chakra-ui/react';
import { CiCalendar } from 'react-icons/ci';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'components/fields/DateField.css';

import {
  formatDateToString,
  formatDateInput,
  parseDateString,
} from 'utils/helpers';
import { useValidationSchema, useValidationEntity } from '../hooks';
import MarketRequiredIndicator from './MarketRequiredIndicator';
import type { ItemPath } from 'api/quote-marketplace';

interface ValidatedDateFieldProps {
  itemPath: ItemPath;
  fieldPath: string;
  label: string;
  date: Date | null;
  onChange: (date: Date | null) => void;
  entity?: Record<string, unknown>;
  isDisabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  labelProps?: Partial<FormLabelProps>;
}

const ValidatedDateField = ({
  itemPath,
  fieldPath,
  label,
  date,
  onChange,
  entity,
  isDisabled,
  minDate,
  maxDate,
  labelProps,
}: ValidatedDateFieldProps) => {
  const { isFieldRequired, isFieldRequiredByMarket } = useValidationSchema();
  const contextEntity = useValidationEntity();

  const resolvedEntity = entity ?? contextEntity ?? {};

  const isUniversalRequired = isFieldRequired(
    itemPath,
    fieldPath,
    resolvedEntity,
  );
  const isMarketRequired = isFieldRequiredByMarket(
    itemPath,
    fieldPath,
    resolvedEntity,
  );
  const isRequired = isUniversalRequired || isMarketRequired;

  const hasValue = date !== null;
  const isInvalid = isRequired && !hasValue;

  const [inputValue, setInputValue] = useState('');
  const [isClamped, setIsClamped] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setInputValue(formatDateToString(date) ?? '');
  }, [date]);

  const isDateInRange = (d: Date): boolean => {
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateInput(e.target.value);
    setInputValue(formatted);
    setIsClamped(false);

    if (formatted.length === 10) {
      const parsedDate = parseDateString(formatted);
      if (parsedDate && isDateInRange(parsedDate)) {
        onChange(parsedDate);
      } else {
        const clamped =
          parsedDate && minDate && parsedDate < minDate
            ? minDate
            : (maxDate ?? new Date());
        setInputValue(formatDateToString(clamped) ?? '');
        setIsClamped(true);
        onChange(clamped);
      }
    } else if (formatted.length === 0) {
      onChange(null);
    }
  };

  const handleDatePickerChange = (selectedDate: Date | null) => {
    if (selectedDate && !isDateInRange(selectedDate)) {
      return;
    }
    setIsClamped(false);
    onChange(selectedDate);
    setIsOpen(false);
  };

  const handleCalendarClick = () => {
    if (!isDisabled) {
      if (!isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(true);
    }
  };

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
    >
      <FormLabel
        fontSize="sm"
        fontWeight="600"
        whiteSpace="nowrap"
        {...labelProps}
      >
        {label}
      </FormLabel>
      <div
        ref={containerRef}
        className="custom-date-picker"
        style={{ position: 'relative' }}
      >
        <InputGroup>
          <Input
            ref={inputRef}
            variant="main"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            placeholder="YYYY-MM-DD"
            disabled={isDisabled}
            title={isDisabled ? 'Editing is disabled' : ''}
            borderColor={isInvalid || isClamped ? 'red.600' : 'inherit'}
            maxLength={10}
          />
          <InputRightElement width="auto" pr={2} zIndex={0}>
            <IconButton
              aria-label="Open calendar"
              icon={<CiCalendar size={20} />}
              size="sm"
              variant="ghost"
              bg="white"
              onClick={handleCalendarClick}
              isDisabled={isDisabled}
            />
          </InputRightElement>
        </InputGroup>
        {isOpen && (
          <Portal>
            <div
              ref={pickerRef}
              className="custom-date-picker"
              style={{
                position: 'absolute',
                top: pickerPosition.top,
                left: pickerPosition.left,
                zIndex: 1400,
              }}
            >
              {/* @ts-ignore */}
              <DatePicker
                selected={date}
                onChange={handleDatePickerChange}
                inline
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                yearDropdownItemNumber={100}
                scrollableYearDropdown
                dateFormat="yyyy-MM-dd"
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          </Portal>
        )}
      </div>
      {!hasValue && (
        <MarketRequiredIndicator
          itemPath={itemPath}
          fieldPath={fieldPath}
          entity={resolvedEntity}
        />
      )}
    </FormControl>
  );
};

export default ValidatedDateField;
